import { buildFullOptions } from '@adornis/data-view/client/data-view-options.js';
import { DataTable } from '@adornis/data-view/client/table/data-table.js';
import { View } from '@adornis/data-view/db/view.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { currentUserSignal } from '@adornis/users/client/currentUser.js';
import { html, nothing } from 'lit';
import { getMailProcessingFilterMonths } from '../../features/information-extraction/api/getMailProcessingFilterMonths.js';
import { getMailProcessingStats } from '../../features/information-extraction/api/getMailProcessingStats.js';
import { getMailProcessingUserStats } from '../../features/information-extraction/api/getMailProcessingUserStats.js';
import {
  MAIL_PROCESSING_EVALUATION_DESCRIPTIONS,
  MAIL_PROCESSING_EVALUATION_DOTS,
  MAIL_PROCESSING_EVALUATION_LABELS,
  MAIL_PROCESSING_NOT_EVALUATED_LABEL,
  MailProcessing,
  MailProcessingEvaluation,
} from '../../features/information-extraction/db/MailExtractionResult.js';
import { MailProcessingFilter, MonthOfYear } from '../../features/information-extraction/db/MailProcessingFilter.js';
import { evaluationRates, formatRate } from '../../features/information-extraction/db/MailProcessingStats.js';
import { getNotificationCount } from '../../features/notifications/api/getNotificationCount.js';

// One tile (and one table column) per rating, in the shared display order. Label, hint and colour
// come from the rating list, so a new category shows up in the statistics on its own.
const RATING_COLUMNS = Object.values(MailProcessingEvaluation).map(value => ({
  key: value,
  label: MAIL_PROCESSING_EVALUATION_LABELS[value],
  hint: MAIL_PROCESSING_EVALUATION_DESCRIPTIONS[value],
  color: MAIL_PROCESSING_EVALUATION_DOTS[value],
}));

// Which counter of MailProcessingStats / MailProcessingUserStats holds which rating.
const COUNTER_BY_RATING = {
  [MailProcessingEvaluation.CORRECT]: 'sumCorrect',
  [MailProcessingEvaluation.CORRECT_AFTER_CORRECTION]: 'sumCorrectAfterCorrection',
  [MailProcessingEvaluation.CORRECT_WITH_ADJUSTMENT]: 'sumCorrectWithAdjustment',
  [MailProcessingEvaluation.FALSE]: 'sumIncorrect',
  [MailProcessingEvaluation.UNSOLVABLE_INQUIRY]: 'sumUnsolvable',
} as const satisfies Record<MailProcessingEvaluation, string>;

const RATE_BASE_HINT =
  `Basis aller Quoten: bewertete Anfragen ohne „${MAIL_PROCESSING_EVALUATION_LABELS[MailProcessingEvaluation.UNSOLVABLE_INQUIRY]}“ — ` +
  'diese Fälle konnte die Extraktion nicht richtig lösen und zählen deshalb weder als Treffer noch als Fehler.';

// The single accent, reused for every derived percentage so rates read as one family.
const RATE_COLOR = '#009ee1';

// A question is raised long before anyone rates the extraction it came from, so the rating
// filter has no counterpart here.
const QUESTION_HINT =
  'Wie oft die Extraktion nachfragen musste — Grundlage für die Abrechnung. Ohne Zeitraum-Filter über alle Monate hinweg.';
const QUESTION_BASE_HINT =
  'Ohne Zeitraum-Filter über alle Monate hinweg. Der Status-Filter bezieht sich auf die Bewertung einer Extraktion und bleibt hier ohne Wirkung.';

const statCard = ({
  label,
  value,
  color,
  valueClass = '',
  hint,
}: {
  label: string;
  value: unknown;
  /** Rating colour, so a tile reads like the dot/pill for the same rating elsewhere. */
  color?: string;
  valueClass?: string;
  hint?: string;
}) => html`
  <div class="card border border-base-300 bg-base-100" title=${hint ?? label}>
    <div class="card-body p-4">
      <div class="text-xs uppercase tracking-wide text-base-content/60 font-medium">${label}</div>
      <div class="text-2xl font-bold mt-auto ${valueClass}" style=${color ? `color:${color}` : nothing}>${value}</div>
    </div>
  </div>
`;

export const StatisticsPage = stateful(() => {
  const [filter] = useState(new MailProcessingFilter({}));

  const { value: statistics, loading: isStatisticsLoading } = useQuery(
    () =>
      getMailProcessingStats(filter)({
        sumCorrect: 1,
        sumCorrectAfterCorrection: 1,
        sumCorrectWithAdjustment: 1,
        sumExtractions: 1,
        sumIncorrect: 1,
        sumUnsolvable: 1,
        sumNotEvaluated: 1,
      }),
    [JSON.stringify(filter.toObject())],
  );

  const { value: filterMonths } = useQuery(
    () =>
      getMailProcessingFilterMonths()({
        month: 1,
        year: 1,
      }),
    [filter],
  );

  // Per-user breakdown (leaderboard). Keyed only on the period/status so the
  // table always lists every user — it is the per-user view itself, never
  // scoped to a single sender.
  const { value: userStats, loading: isUserStatsLoading } = useQuery(
    () =>
      getMailProcessingUserStats(filter)({
        senderAddress: 1,
        userName: 1,
        sumExtractions: 1,
        sumCorrect: 1,
        sumCorrectAfterCorrection: 1,
        sumCorrectWithAdjustment: 1,
        sumIncorrect: 1,
        sumUnsolvable: 1,
        sumNotEvaluated: 1,
      }),
    [JSON.stringify({ m: filter.monthOfYear?.toObject(), e: filter.evaluation })],
  );

  // How often the platform had to ask — the number the customer is billed by.
  const { value: questionCount, loading: isQuestionCountLoading } = useQuery(
    () => getNotificationCount(filter)(),
    [JSON.stringify(filter.toObject())],
  );

  const myEmail = currentUserSignal.value?.email?.toLowerCase();

  const controller = useForm(filter);

  return html`
    <div class="p-6 max-w-6xl mx-auto">
      <h1 class="text-3xl font-bold mb-6 flex items-center gap-3">
        <x-icon class="text-base-content/70">bar_chart</x-icon>
        Extraktions-Statistiken
      </h1>

      <div class="flex items-end gap-4 pb-4 border-b border-base-300 mb-6">
        <div>
          <label class="label-text font-medium mb-1 block">Zeitraum</label>
          ${DropdownSelectionInput<MonthOfYear>({
            ...controller.field('monthOfYear'),
            selectables: filterMonths ?? [],
            renderString: m => {
              const MONTHS = [
                'Januar',
                'Februar',
                'März',
                'April',
                'Mai',
                'Juni',
                'Juli',
                'August',
                'September',
                'Oktober',
                'November',
                'Dezember',
              ];
              return `${MONTHS[m.month - 1]} ${m.year}`;
            },
            resolveID: m => `${m.year}-${m.month}`,
            clearable: true,
            placeholder: 'Monat filtern',
          })}
        </div>
        <div>
          <label class="label-text font-medium mb-1 block">Status</label>
          ${DropdownSelectionInput({
            ...controller.field('evaluation'),
            selectables: Object.values(MailProcessingEvaluation),
            renderString: (e: MailProcessingEvaluation) => MAIL_PROCESSING_EVALUATION_LABELS[e],
            clearable: true,
            placeholder: 'Status',
          })}
        </div>
      </div>

      <!-- Stats -->
      ${isStatisticsLoading
        ? html`<div class="flex justify-center py-4">
            <span class="loading loading-spinner loading-md text-primary"></span>
          </div>`
        : statistics
          ? html`
              ${(() => {
                const rates = evaluationRates(statistics);
                return html`
                  <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-3">
                    ${statCard({ label: 'Extraktionen', value: statistics.sumExtractions ?? 0 })}
                    ${RATING_COLUMNS.map(c =>
                      statCard({
                        label: c.label,
                        value: statistics[COUNTER_BY_RATING[c.key]] ?? 0,
                        color: c.color,
                        hint: c.hint,
                      }),
                    )}
                    ${statCard({
                      label: MAIL_PROCESSING_NOT_EVALUATED_LABEL,
                      value: statistics.sumNotEvaluated ?? 0,
                      valueClass: 'text-base-content/50',
                    })}
                    ${statCard({
                      label: 'Korrekt ohne Eingriff in %',
                      value: formatRate(rates.straightThrough),
                      color: RATE_COLOR,
                      hint: `Anteil, der ohne jeden Eingriff durchlief. ${RATE_BASE_HINT}`,
                    })}
                    ${statCard({
                      label: 'Extraktion korrekt in %',
                      value: formatRate(rates.extractionCorrect),
                      color: RATE_COLOR,
                      hint: `Anteil, bei dem die Extraktion selbst richtig war — inklusive der Fälle, die nur aus fachlichen Gründen angepasst wurden. ${RATE_BASE_HINT}`,
                    })}
                    ${statCard({
                      label: 'Korrekt nach Prüfung in %',
                      value: formatRate(rates.correctAfterReview),
                      color: RATE_COLOR,
                      hint: `Anteil, der nach der manuellen Prüfung richtig war — unabhängig davon, wie viel Eingriff nötig war. ${RATE_BASE_HINT}`,
                    })}
                  </div>
                  <div class="text-xs text-base-content/50 mb-6">
                    Quoten-Basis: ${rates.attributable} bewertete ${rates.attributable === 1 ? 'Anfrage' : 'Anfragen'}
                    (ohne „${MAIL_PROCESSING_EVALUATION_LABELS[MailProcessingEvaluation.UNSOLVABLE_INQUIRY]}“ und
                    „${MAIL_PROCESSING_NOT_EVALUATED_LABEL}“).
                  </div>
                `;
              })()}
            `
          : null}

      <!-- Rückfragen: how often the platform had to ask, as the billing basis. -->
      <div class="flex items-center gap-2 mt-2 mb-3">
        <x-icon class="text-base text-base-content/70">forum</x-icon>
        <h2 class="text-lg font-semibold">Rückfragen</h2>
      </div>
      ${isQuestionCountLoading
        ? html`<div class="flex justify-center py-4">
            <span class="loading loading-spinner loading-md text-primary"></span>
          </div>`
        : html`
            <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-3">
              ${statCard({ label: 'Gestellte Rückfragen', value: questionCount ?? 0, hint: QUESTION_HINT })}
            </div>
            <div class="text-xs text-base-content/50 mb-8">${QUESTION_BASE_HINT}</div>
          `}

      <!-- Per-user breakdown: the same counters grouped by the employee who
           forwarded the request; the current user's row is highlighted. -->
      <div class="flex items-center gap-2 mt-2 mb-3">
        <x-icon class="text-base text-base-content/70">groups</x-icon>
        <h2 class="text-lg font-semibold">Pro Nutzer</h2>
      </div>
      ${isUserStatsLoading
        ? html`<div class="flex justify-center py-4">
            <span class="loading loading-spinner loading-md text-primary"></span>
          </div>`
        : userStats?.length
          ? html`
              <div class="overflow-x-auto border border-base-300 rounded-lg mb-8">
                <table class="table table-sm">
                  <thead>
                    <tr class="text-base-content/60">
                      <th>Nutzer</th>
                      <th class="text-right">Extraktionen</th>
                      ${RATING_COLUMNS.map(c => html`<th class="text-right" title=${c.hint}>${c.label}</th>`)}
                      <th class="text-right">${MAIL_PROCESSING_NOT_EVALUATED_LABEL}</th>
                      <th class="text-right" title=${RATE_BASE_HINT}>Ohne Eingriff in %</th>
                      <th class="text-right" title=${RATE_BASE_HINT}>Extraktion korrekt in %</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${userStats.map(u => {
                      const rates = evaluationRates(u);
                      const isMe = !!myEmail && u.senderAddress?.toLowerCase() === myEmail;
                      return html`
                        <tr class=${isMe ? 'bg-primary/5' : ''}>
                          <td class="font-medium ${isMe ? 'text-primary' : ''}">
                            ${u.userName ?? u.senderAddress}${isMe
                              ? html`<span class="ml-1 text-xs text-primary/70">(ich)</span>`
                              : ''}
                          </td>
                          <td class="text-right tabular-nums">${u.sumExtractions ?? 0}</td>
                          ${RATING_COLUMNS.map(
                            c =>
                              html`<td class="text-right tabular-nums" style="color:${c.color}">
                                ${u[COUNTER_BY_RATING[c.key]] ?? 0}
                              </td>`,
                          )}
                          <td class="text-right tabular-nums text-base-content/50">${u.sumNotEvaluated ?? 0}</td>
                          <td class="text-right tabular-nums font-semibold">${formatRate(rates.straightThrough)}</td>
                          <td class="text-right tabular-nums font-semibold">${formatRate(rates.extractionCorrect)}</td>
                        </tr>
                      `;
                    })}
                  </tbody>
                </table>
              </div>
            `
          : html`<div class="text-sm text-base-content/50 mb-8">Keine nutzerbezogenen Daten im gewählten Zeitraum.</div>`}

      <div class="flex items-center gap-2 mt-2 mb-3">
        <x-icon class="text-base text-base-content/70">list</x-icon>
        <h2 class="text-lg font-semibold">Alle Extraktionen</h2>
      </div>
      ${DataTable({
        options: buildFullOptions({
          class: MailProcessing,
          view: new View({ filter }),
          operation: 'subscribeMailProcessings',
          selectionSet: {
            evaluation: 1,
            createdAt: 1,
            mailID: 1,
          },
          customColumns: [
            {
              fieldName: 'evaluation',
              columnName: 'Bewertung',
              getFieldMapping: (mp: MailProcessing) =>
                mp.evaluation ? MAIL_PROCESSING_EVALUATION_LABELS[mp.evaluation] : '–',
            },
            { fieldName: 'createdAt' },
          ],
          entryClickHandler(entity, column) {
            goTo(`/mails/${entity.mailID}`);
          },
        }).dataViewOptions,
      })}
    </div>
  `;
});
